import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  HelpCircle,
  Link2,
  PlugZap,
  RefreshCw,
  Save,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatUkDateTime } from "@/lib/date";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const emptyHub = {
  dashboard: {},
  catalog: [],
  connections: [],
  sync_runs: [],
  api_keys: [],
  webhooks: [],
  logs: [],
  settings: {},
  health: [],
};

const initialWizard = {
  open: false, step: 1, provider_id: "companies_house", environment: "production", sync_frequency: "manual",
  api_key: "", client_id: "", client_secret: "", redirect_uri: "", host: "", port: 587, username: "", password: "",
  sender_email: "", sender_name: "EPOS Accountancy", use_tls: true, aws_iam_secret: false, model: "gpt-5.6-luna",
  product_name: "EPOS Accountancy", product_version: "",
  filing_enabled: false, filing_environment: "test", presenter_id: "", presenter_code: "", confirm_filing_enablement: false,
  tested: false, existing_id: null,
};

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function providerOptions(catalog) {
  return safeArray(catalog).flatMap((group) =>
    safeArray(group?.providers).map((provider) => ({ ...provider, category: group?.category }))
  );
}

function statusClass(status) {
  if (["connected", "success", "active", "healthy", "authenticated"].includes(status)) return "bg-emerald-100 text-emerald-800";
  if (["configured", "unknown", "manual", "planned"].includes(status)) return "bg-stone-100 text-stone-700";
  if (["disabled", "not_authenticated", "attention", "failed", "disconnected"].includes(status)) return "bg-amber-100 text-amber-800";
  return "bg-stone-100 text-stone-700";
}

function KpiCard({ label, value, icon: Icon, tone = "emerald" }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    red: "bg-red-50 text-red-700 ring-red-100",
    stone: "bg-stone-100 text-stone-600 ring-stone-200",
  };
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-[0_3px_12px_rgba(28,25,23,0.06)]">
      <div className="flex items-center gap-3">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ring-1 ${tones[tone] || tones.stone}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</p>
          <p className="mt-1 truncate font-display text-xl font-bold text-stone-950">{value ?? 0}</p>
        </div>
      </div>
    </div>
  );
}

function Panel({ title, subtitle, children, action }) {
  return (
    <section className="rounded-lg border border-stone-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b border-stone-100 px-4 py-3">
        <div>
          <h2 className="font-display text-base font-bold text-stone-900">{title}</h2>
          {subtitle && <p className="mt-1 text-sm text-stone-500">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function EmptyState({ text }) {
  return <div className="rounded-lg border border-dashed border-stone-200 bg-stone-50 px-4 py-8 text-center text-sm text-stone-500">{text}</div>;
}

function ConnectionCard({ connection, onAction, onSync, onConfigure }) {
  return (
    <div className="flex min-h-[250px] flex-col rounded-xl border border-stone-200 bg-white p-4 shadow-[0_3px_12px_rgba(28,25,23,0.07)]">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">
            <PlugZap className="h-6 w-6" />
          </span>
          <div className="min-w-0 pt-0.5">
            <h3 className="truncate font-display text-base font-bold text-stone-950">{connection.provider_name}</h3>
            <p className="mt-1 truncate text-xs text-stone-500">{connection.category} - {connection.environment}</p>
            <p className="mt-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">Connected service</p>
          </div>
        </div>
        <Badge className={statusClass(connection.disabled ? "disabled" : connection.status)}>{connection.disabled ? "Disabled" : connection.status}</Badge>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 border-t border-stone-200 pt-3 text-sm">
        <div>
          <p className="text-xs font-semibold uppercase text-stone-400">Last sync</p>
          <p className="text-stone-700">{formatUkDateTime(connection.last_sync_at, "Not synced")}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase text-stone-400">Next sync</p>
          <p className="text-stone-700">{formatUkDateTime(connection.next_sync_at, "Manual")}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase text-stone-400">Auth</p>
          <p className="text-stone-700">{connection.authentication_status || "Unknown"}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase text-stone-400">Health</p>
          <p className="text-stone-700">{connection.health_status || "Unknown"}</p>
        </div>
      </div>
      <div className="mt-auto flex flex-wrap gap-2 border-t border-stone-100 pt-3">
        <Button size="sm" variant="outline" onClick={() => onAction(connection.id, "test")}>Test</Button>
        <Button size="sm" variant="outline" onClick={() => onConfigure(connection)}>Configure</Button>
        <Button size="sm" variant="outline" onClick={() => onSync(connection.id)}>Sync</Button>
        <Button size="sm" variant="outline" onClick={() => onAction(connection.id, connection.disabled ? "enable" : "disable")}>
          {connection.disabled ? "Enable" : "Disable"}
        </Button>
      </div>
    </div>
  );
}

export default function AdminIntegrationHub() {
  const [hub, setHub] = useState(emptyHub);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wizard, setWizard] = useState(initialWizard);
  const [helpTopic, setHelpTopic] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState({});
  const [providerConfigs, setProviderConfigs] = useState({});

  const providers = useMemo(() => providerOptions(hub.catalog), [hub.catalog]);
  const connections = safeArray(hub.connections);
  const activeConnections = connections.filter((connection) => !connection.disabled && connection.status === "connected" && connection.health_status === "healthy");
  const availableProviders = providers.filter((provider) => provider.status === "available" && !activeConnections.some((connection) => connection.provider_id === provider.id));
  const logs = safeArray(hub.logs);

  async function loadHub() {
    try {
      const [{ data }, quickbooks, xero, sage, companiesHouse, smtp, openai, hmrc] = await Promise.all([
        api.get("/admin/integration-hub"),
        api.get("/admin/integrations/quickbooks/config"), api.get("/admin/integrations/xero/config"), api.get("/admin/integrations/sage/config"),
        api.get("/admin/integrations/companies-house/config"), api.get("/admin/settings/smtp"), api.get("/admin/settings/openai"), api.get("/admin/settings/hmrc-platform"),
      ]);
      setHub({ ...emptyHub, ...(data || {}) });
      setSettingsDraft(data?.settings || {});
      setProviderConfigs({ quickbooks: quickbooks.data, xero: xero.data, sage: sage.data, companies_house: companiesHouse.data, smtp: smtp.data, openai: openai.data, hmrc_mtd: hmrc.data });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadHub();
  }, []);

  function openWizard(provider, connection = null) {
    if (!provider) return;
    const config = providerConfigs[provider.id] || {};
    setWizard({
      ...initialWizard, open: true, provider_id: provider.id,
      environment: connection?.environment || config.environment || (provider.id === "quickbooks" ? "sandbox" : "production"),
      sync_frequency: connection?.sync_frequency || "manual", existing_id: connection?.id || null,
      client_id: config.client_id || (config.client_id_saved ? "saved" : ""), redirect_uri: config.redirect_uri || "",
      host: config.host || "", port: config.port || 587, username: config.username || "", sender_email: config.sender_email || "", sender_name: config.sender_name || "EPOS Accountancy", use_tls: config.use_tls !== false,
      model: config.model || "gpt-5.6-luna",
      product_name: config.product_name || "EPOS Accountancy", product_version: config.product_version || "",
      filing_enabled: config.filing_enabled === true, filing_environment: config.filing_environment || "test", presenter_id: config.presenter_id || "", confirm_filing_enablement: false,
    });
  }

  async function saveAndTestProvider(values) {
    const provider = values.provider_id;
    if (["quickbooks", "xero", "sage"].includes(provider)) {
      await api.put(`/admin/integrations/${provider}/config`, {
        client_id: values.client_id === "saved" ? "" : values.client_id, client_secret: values.client_secret,
        environment: values.environment, redirect_uri: values.redirect_uri, enabled: true,
      });
      return api.post(`/admin/integrations/${provider}/config/test`, {});
    }
    if (provider === "smtp") {
      const payload = { host: values.host, port: Number(values.port), username: values.username, password: values.password || undefined, sender_email: values.sender_email, sender_name: values.sender_name, use_tls: values.use_tls, aws_iam_secret: values.aws_iam_secret };
      await api.put("/admin/settings/smtp", payload);
      return api.post("/admin/settings/smtp/test", {});
    }
    if (provider === "openai") {
      await api.put("/admin/settings/openai", { api_key: values.api_key || undefined, model: values.model });
      return api.post("/admin/settings/openai/test", {});
    }
    if (provider === "companies_house") {
      await api.post("/admin/integrations/companies-house/config/test", { api_key: values.api_key || null });
      await api.put("/admin/integrations/companies-house/config", {
        api_key: values.api_key || undefined, enabled: true, filing_enabled: values.filing_enabled,
        filing_environment: values.filing_environment, presenter_id: values.presenter_id,
        presenter_code: values.presenter_code || undefined, confirm_filing_enablement: values.confirm_filing_enablement,
      });
      return;
    }
    if (provider === "hmrc_mtd") {
      await api.put("/admin/settings/hmrc-platform", {
        enabled: true,
        environment: values.environment,
        client_id: values.client_id === "saved" ? "" : values.client_id,
        client_secret: values.client_secret || undefined,
        redirect_uri: values.redirect_uri,
        product_name: values.product_name,
        product_version: values.product_version,
      });
      return api.post("/admin/settings/hmrc-platform/test", {});
    }
    throw new Error("This integration does not yet have a tested connection adapter.");
  }

  async function testWizardConnection() {
    setSaving(true);
    try {
      await saveAndTestProvider(wizard);
      setWizard((current) => ({ ...current, tested: true, step: 4 }));
      toast.success("Connection test succeeded");
    } catch (err) {
      toast.error(formatApiError(err), { duration: 9000 });
    } finally {
      setSaving(false);
    }
  }

  async function completeWizard() {
    if (!wizard.tested) return;
    setSaving(true);
    try {
      const needsTenantAuthorisation = wizard.provider_id === "hmrc_mtd";
      await api.post("/admin/integration-hub/connections", {
        provider_id: wizard.provider_id,
        environment: wizard.environment,
        sync_frequency: wizard.sync_frequency,
        has_credentials: true,
        status: needsTenantAuthorisation ? "configured" : "connected",
        authentication_status: needsTenantAuthorisation ? "application_configured" : "authenticated",
        health_status: needsTenantAuthorisation ? "attention" : "healthy",
      });
      toast.success(needsTenantAuthorisation ? "HMRC application saved; tenant authorisation and sandbox testing are still required" : "Integration connected");
      setWizard(initialWizard);
      await loadHub();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function runAction(connectionId, action) {
    try {
      const connection = connections.find((item) => item.id === connectionId);
      if (action === "test" && connection) await saveAndTestProvider({ ...initialWizard, provider_id: connection.provider_id, environment: connection.environment, ...providerConfigs[connection.provider_id] });
      await api.post(`/admin/integration-hub/connections/${connectionId}/${action}`, {});
      toast.success("Connection updated");
      await loadHub();
    } catch (err) {
      if (action === "test") {
        try { await api.post(`/admin/integration-hub/connections/${connectionId}/disable`, {}); await loadHub(); } catch { /* keep the original test error */ }
      }
      toast.error(formatApiError(err));
    }
  }

  async function runSync(connectionId) {
    try {
      const { data } = await api.post("/admin/integration-hub/sync", { connection_id: connectionId, sync_type: "manual" });
      const run = safeArray(data?.sync_runs).find((item) => item.connection_id === connectionId);
      if (run?.status === "failed") toast.error(run.error_details || "Sync failed");
      else toast.success("Sync completed");
      await loadHub();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function saveSettings() {
    setSaving(true);
    try {
      await api.put("/admin/integration-hub/settings", settingsDraft);
      toast.success("Integration Hub settings saved");
      await loadHub();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="p-6 text-stone-500">Loading Integration Hub...</div>;
  }

  return (
    <div className="mx-auto flex max-w-[1680px] flex-col gap-4 p-2 sm:p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-stone-950">Integration Hub</h1>
          <p className="text-sm text-stone-500">Platform-owned connections, encrypted credentials, synchronisation and security monitoring. Practice users cannot change shared provider settings.</p>
        </div>
        <Button onClick={loadHub} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      <Tabs defaultValue="dashboard" className="space-y-4">
        <TabsList className="flex h-auto flex-wrap justify-start gap-2 bg-stone-100 p-1">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="connected">Connected Services</TabsTrigger>
          <TabsTrigger value="available">Available Integrations</TabsTrigger>
          <TabsTrigger value="logs">Logs</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
            <KpiCard label="Active integrations" value={hub.dashboard?.active_integrations} icon={PlugZap} />
            <KpiCard label="Syncs today" value={hub.dashboard?.syncs_today} icon={RefreshCw} />
            <KpiCard label="Failed syncs" value={hub.dashboard?.failed_syncs} icon={AlertTriangle} tone={hub.dashboard?.failed_syncs ? "red" : "stone"} />
            <KpiCard label="Pending actions" value={hub.dashboard?.pending_actions} icon={Clock} tone={hub.dashboard?.pending_actions ? "amber" : "stone"} />
            <KpiCard label="API usage" value={hub.dashboard?.api_usage} icon={Activity} tone="stone" />
            <KpiCard label="Last success" value={hub.dashboard?.last_successful_sync ? "Saved" : "None"} icon={CheckCircle2} tone="stone" />
          </div>
          <div className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
            <Panel title="Recent activity">
              {logs.length ? (
                <div className="divide-y divide-stone-100">
                  {logs.slice(0, 8).map((log) => (
                    <div key={log.id} className="flex items-center justify-between gap-3 py-3 text-sm">
                      <div>
                        <p className="font-semibold text-stone-900">{log.action}</p>
                        <p className="text-stone-500">{log.provider_id || "integration hub"} - {formatUkDateTime(log.created_at)}</p>
                      </div>
                      <Badge className={statusClass(log.status)}>{log.status}</Badge>
                    </div>
                  ))}
                </div>
              ) : <EmptyState text="No integration activity recorded yet." />}
            </Panel>
            <Panel title="Health status">
              {safeArray(hub.health).length ? safeArray(hub.health).map((item, index) => (
                <div key={`${item.provider}-${index}`} className="mb-3 rounded-lg border border-stone-100 bg-stone-50 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-stone-900">{item.provider}</p>
                    <Badge className={statusClass(item.status)}>{item.status}</Badge>
                  </div>
                  <p className="mt-1 text-sm text-stone-500">{item.message}</p>
                </div>
              )) : <EmptyState text="Connect a service to begin health monitoring." />}
            </Panel>
          </div>
        </TabsContent>

        <TabsContent value="connected">
          <Panel title="Connected services" subtitle="Configured integrations are tested, disabled, reconnected and synced from here.">
            {activeConnections.length ? (
              <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
                {activeConnections.map((connection) => (
                  <ConnectionCard key={connection.id} connection={connection} onAction={runAction} onSync={runSync} onConfigure={(item) => openWizard(providers.find((provider) => provider.id === item.provider_id), item)} />
                ))}
              </div>
            ) : <EmptyState text="No connected services yet. Add and test one from Available Integrations." />}
          </Panel>
        </TabsContent>

        <TabsContent value="available">
          <div className="space-y-4">
            {safeArray(hub.catalog).map((group) => {
              const groupProviders = availableProviders.filter((provider) => provider.category === group.category);
              return groupProviders.length ? (
              <Panel key={group.category} title={group.category}>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                  {groupProviders.map((provider) => (
                    <div key={provider.id} className="flex min-h-[190px] flex-col rounded-xl border border-stone-200 bg-white p-4 shadow-[0_3px_12px_rgba(28,25,23,0.07)]">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">
                            <Link2 className="h-6 w-6" />
                          </span>
                          <div className="min-w-0 pt-0.5">
                            <h3 className="truncate font-display text-base font-bold text-stone-950">{provider.name}</h3>
                            <p className="mt-1 text-xs text-stone-500">{provider.auth_type} authentication</p>
                            <p className="mt-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">Available integration</p>
                          </div>
                        </div>
                        <Badge className={statusClass(provider.status)}>{provider.status}</Badge>
                      </div>
                      <Button className="mt-auto w-full gap-2 border-emerald-200 text-emerald-800 hover:bg-emerald-50" variant="outline" onClick={() => openWizard(provider, connections.find((connection) => connection.provider_id === provider.id))} disabled={saving}>
                        <Link2 className="h-4 w-4" /> Add connection
                      </Button>
                    </div>
                  ))}
                </div>
              </Panel>
              ) : null;
            })}
            {!availableProviders.length && <EmptyState text="Every currently supported integration is connected. Disable one to return it here." />}
          </div>
        </TabsContent>

        <TabsContent value="logs">
          <Panel title="Integration logs" subtitle="Complete provider action log with status, duration and error detail.">
            {logs.length ? (
              <div className="divide-y divide-stone-100">
                {logs.map((log) => (
                  <div key={log.id} className="grid gap-2 py-3 text-sm md:grid-cols-[180px_1fr_120px_120px]">
                    <span className="text-stone-500">{formatUkDateTime(log.created_at)}</span>
                    <span><strong>{log.provider_id || "hub"}</strong> - {log.action}{log.error_details ? ` - ${log.error_details}` : ""}</span>
                    <Badge className={statusClass(log.status)}>{log.status}</Badge>
                    <span>{log.duration_ms || 0} ms</span>
                  </div>
                ))}
              </div>
            ) : <EmptyState text="No logs yet." />}
          </Panel>
        </TabsContent>

        <TabsContent value="settings">
          <Panel title="Settings" subtitle="Default sync behaviour, retry policy, timeout and audit retention.">
            <div className="grid max-w-4xl gap-4 md:grid-cols-2">
              <div>
                <SettingLabel label="Default sync frequency" topic="sync_frequency" onHelp={setHelpTopic} />
                <select className="mt-1 h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm shadow-sm" value={settingsDraft.default_sync_frequency || "manual"} onChange={(event) => setSettingsDraft((current) => ({ ...current, default_sync_frequency: event.target.value }))}>
                  <option value="manual">Manual</option>
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              <div>
                <SettingLabel label="Notification preferences" topic="notifications" onHelp={setHelpTopic} />
                <Input className="mt-1" value={settingsDraft.notification_preferences || ""} onChange={(event) => setSettingsDraft((current) => ({ ...current, notification_preferences: event.target.value }))} />
              </div>
              <div>
                <SettingLabel label="Retry policy" topic="retry" onHelp={setHelpTopic} />
                <Input className="mt-1" value={settingsDraft.retry_policy || ""} onChange={(event) => setSettingsDraft((current) => ({ ...current, retry_policy: event.target.value }))} />
              </div>
              <div>
                <SettingLabel label="Timeout seconds" topic="timeout" onHelp={setHelpTopic} />
                <Input className="mt-1" type="number" value={settingsDraft.timeout_seconds || 30} onChange={(event) => setSettingsDraft((current) => ({ ...current, timeout_seconds: event.target.value }))} />
              </div>
            </div>
            <p className="mt-4 rounded-lg border border-sky-100 bg-sky-50 p-3 text-sm text-sky-950">Audit retention, secret rotation, cookie policy and webhook network restrictions are managed once in Platform Settings → Security.</p>
            <Button onClick={saveSettings} disabled={saving} className="mt-5 gap-2"><Save className="h-4 w-4" /> Save settings</Button>
          </Panel>
        </TabsContent>
      </Tabs>

      <Dialog open={wizard.open} onOpenChange={(open) => { if (!open) setWizard(initialWizard); }}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Connect {providers.find((provider) => provider.id === wizard.provider_id)?.name || "integration"}</DialogTitle>
          </DialogHeader>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-stone-500">
            {["Options", "Credentials", "Test", "Complete"].map((label, index) => <span key={label} className={wizard.step === index + 1 ? "text-emerald-700" : ""}>{index + 1}. {label}</span>)}
          </div>
          {wizard.step === 1 && <div className="space-y-4">
            <div><Label>Environment</Label><select className="mt-1 h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm" value={wizard.environment} onChange={(event) => setWizard((current) => ({ ...current, environment: event.target.value }))}><option value="production">Production</option>{["quickbooks", "hmrc_mtd"].includes(wizard.provider_id) && <option value="sandbox">Sandbox / development</option>}{wizard.provider_id === "companies_house" && <option value="development">Development</option>}</select></div>
            <div><Label>Sync frequency</Label><select className="mt-1 h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm" value={wizard.sync_frequency} onChange={(event) => setWizard((current) => ({ ...current, sync_frequency: event.target.value }))}><option value="manual">Manual</option><option value="daily">Daily</option><option value="weekly">Weekly</option></select></div>
          </div>}
          {wizard.step === 2 && <div className="space-y-4">
            {wizard.provider_id === "companies_house" && <>
              <div><Label>Companies House API key</Label><Input className="mt-1" type="password" autoComplete="new-password" placeholder={wizard.existing_id ? "Leave blank to use the saved key" : "Paste API key"} value={wizard.api_key} onChange={(event) => setWizard((current) => ({ ...current, api_key: event.target.value, tested: false }))} /></div>
              <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-950"><ShieldCheck className="mr-2 inline h-4 w-4" />Companies House public-data requests use one API key. The password portion of Basic authentication is blank, so no API secret is required.</div>
              <label className="flex items-start gap-2 rounded-lg border border-stone-200 p-3 text-sm"><input className="mt-1" type="checkbox" checked={wizard.filing_enabled} onChange={(event) => setWizard((current) => ({ ...current, filing_enabled: event.target.checked, tested: false }))} /><span><strong>Configure software filing</strong><br/><span className="text-stone-500">Separate from the public lookup API. Requires approved presenter credentials and validated iXBRL filing packages.</span></span></label>
              {wizard.filing_enabled && <>
                <div><Label>Filing environment</Label><select className="mt-1 h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm" value={wizard.filing_environment} onChange={(event) => setWizard((current) => ({ ...current, filing_environment: event.target.value, tested: false }))}><option value="test">Test</option><option value="production">Production</option></select></div>
                <div><Label>Presenter ID</Label><Input className="mt-1" value={wizard.presenter_id} onChange={(event) => setWizard((current) => ({ ...current, presenter_id: event.target.value, tested: false }))} /></div>
                <div><Label>Presenter authentication code</Label><Input className="mt-1" type="password" autoComplete="new-password" value={wizard.presenter_code} onChange={(event) => setWizard((current) => ({ ...current, presenter_code: event.target.value, tested: false }))} placeholder={providerConfigs.companies_house?.presenter_code_saved ? "Leave blank to keep the encrypted code" : "Presenter authentication code"} /></div>
                {wizard.filing_environment === "production" && <label className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm"><input className="mt-1" type="checkbox" checked={wizard.confirm_filing_enablement} onChange={(event) => setWizard((current) => ({ ...current, confirm_filing_enablement: event.target.checked, tested: false }))} /><span><strong>Confirm production enablement</strong><br/><span className="text-stone-600">I confirm presenter approval and Companies House package validation are recorded in Platform security.</span></span></label>}
              </>}
            </>}
            {wizard.provider_id === "hmrc_mtd" && <>
              <div><Label>HMRC OAuth client ID</Label><Input className="mt-1" value={wizard.client_id} onChange={(event) => setWizard((current) => ({ ...current, client_id: event.target.value, tested: false }))} placeholder="Application client ID issued by HMRC" /></div>
              <div><Label>HMRC OAuth client secret</Label><Input className="mt-1" type="password" autoComplete="new-password" value={wizard.client_secret} onChange={(event) => setWizard((current) => ({ ...current, client_secret: event.target.value, tested: false }))} placeholder={providerConfigs.hmrc_mtd?.client_secret_saved ? "Leave blank to keep the encrypted secret" : "Application client secret issued by HMRC"} /></div>
              <div><Label>HTTPS redirect URI</Label><Input className="mt-1" value={wizard.redirect_uri} onChange={(event) => setWizard((current) => ({ ...current, redirect_uri: event.target.value, tested: false }))} /></div>
              <div className="grid grid-cols-2 gap-3"><div><Label>Product name</Label><Input className="mt-1" value={wizard.product_name} onChange={(event) => setWizard((current) => ({ ...current, product_name: event.target.value, tested: false }))} /></div><div><Label>Product version</Label><Input className="mt-1" value={wizard.product_version} onChange={(event) => setWizard((current) => ({ ...current, product_version: event.target.value, tested: false }))} /></div></div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">These are platform-owned application credentials. Each practice/client must still complete its own HMRC OAuth authorisation. Production remains blocked until HMRC approval and fraud-header validation are recorded.</div>
            </>}
            {["quickbooks", "xero", "sage"].includes(wizard.provider_id) && <>
              <div><Label>Client ID</Label><Input className="mt-1" value={wizard.client_id} onChange={(event) => setWizard((current) => ({ ...current, client_id: event.target.value, tested: false }))} placeholder="OAuth app client ID" /></div>
              <div><Label>Client secret</Label><Input className="mt-1" type="password" autoComplete="new-password" value={wizard.client_secret} onChange={(event) => setWizard((current) => ({ ...current, client_secret: event.target.value, tested: false }))} placeholder={providerConfigs[wizard.provider_id]?.client_secret_saved ? "Leave blank to keep the saved secret" : "OAuth app client secret"} /></div>
              <div><Label>Redirect URI</Label><Input className="mt-1" value={wizard.redirect_uri} onChange={(event) => setWizard((current) => ({ ...current, redirect_uri: event.target.value, tested: false }))} /></div>
              <div className="rounded-lg border border-sky-100 bg-sky-50 p-3 text-sm text-sky-950">This validates the shared OAuth app settings. Each client still selects and authorises their own QuickBooks, Xero or Sage organisation from the client account.</div>
            </>}
            {wizard.provider_id === "smtp" && <>
              <div className="grid gap-3 sm:grid-cols-2"><div><Label>SMTP host</Label><Input className="mt-1" value={wizard.host} onChange={(event) => setWizard((current) => ({ ...current, host: event.target.value, tested: false }))} /></div><div><Label>Port</Label><Input className="mt-1" type="number" value={wizard.port} onChange={(event) => setWizard((current) => ({ ...current, port: event.target.value, tested: false }))} /></div></div>
              <div><Label>Username</Label><Input className="mt-1" value={wizard.username} onChange={(event) => setWizard((current) => ({ ...current, username: event.target.value, tested: false }))} /></div>
              <div><Label>Password</Label><Input className="mt-1" type="password" autoComplete="new-password" value={wizard.password} onChange={(event) => setWizard((current) => ({ ...current, password: event.target.value, tested: false }))} placeholder={providerConfigs.smtp?.configured ? "Leave blank to keep the saved password" : "SMTP password"} /></div>
              <div className="grid gap-3 sm:grid-cols-2"><div><Label>Sender email</Label><Input className="mt-1" type="email" value={wizard.sender_email} onChange={(event) => setWizard((current) => ({ ...current, sender_email: event.target.value, tested: false }))} /></div><div><Label>Sender name</Label><Input className="mt-1" value={wizard.sender_name} onChange={(event) => setWizard((current) => ({ ...current, sender_name: event.target.value, tested: false }))} /></div></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={wizard.use_tls} onChange={(event) => setWizard((current) => ({ ...current, use_tls: event.target.checked, tested: false }))} /> Use TLS</label>
            </>}
            {wizard.provider_id === "openai" && <>
              <div><Label>OpenAI API key</Label><Input className="mt-1" type="password" autoComplete="new-password" value={wizard.api_key} onChange={(event) => setWizard((current) => ({ ...current, api_key: event.target.value, tested: false }))} placeholder={providerConfigs.openai?.configured ? "Leave blank to keep the saved key" : "sk-..."} /></div>
              <div><Label>Model</Label><Input className="mt-1" value={wizard.model} onChange={(event) => setWizard((current) => ({ ...current, model: event.target.value, tested: false }))} /></div>
            </>}
            {wizard.provider_id === "hmrc_mtd" && <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950"><AlertTriangle className="mr-2 inline h-4 w-4" />This validates platform application settings only. HMRC remains in Available Integrations until tenant OAuth, fraud-header and sandbox endpoint checks are complete.</div>}
          </div>}
          {wizard.step === 3 && <div className="rounded-lg border border-stone-200 bg-stone-50 p-4 text-sm text-stone-700">The connection test uses the provider-specific adapter. Failed tests stay in Available Integrations; only a successful test can be completed.</div>}
          {wizard.step === 4 && <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950"><CheckCircle2 className="mr-2 inline h-5 w-5" />{wizard.provider_id === "hmrc_mtd" ? "Application settings passed local validation. Complete to save the configuration; it will remain available until tenant authorisation and sandbox testing pass." : "Connection test passed. Complete the wizard to move this service to Connected Services."}</div>}
          <DialogFooter>
            {wizard.step > 1 && wizard.step < 4 && <Button variant="outline" onClick={() => setWizard((current) => ({ ...current, step: current.step - 1 }))}>Back</Button>}
            {wizard.step < 3 && <Button onClick={() => setWizard((current) => ({ ...current, step: current.step + 1 }))}>Continue</Button>}
            {wizard.step === 3 && <Button onClick={testWizardConnection} disabled={saving}>{saving ? "Testing…" : "Test connection"}</Button>}
            {wizard.step === 4 && <Button onClick={completeWizard} disabled={saving}>{saving ? "Completing…" : "Complete connection"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!helpTopic} onOpenChange={(open) => { if (!open) setHelpTopic(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{HELP_TOPICS[helpTopic]?.title || "Integration setting"}</DialogTitle></DialogHeader>
          <p className="text-sm leading-6 text-stone-600">{HELP_TOPICS[helpTopic]?.body}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const HELP_TOPICS = {
  sync_frequency: { title: "Default sync frequency", body: "The starting cadence suggested for new connections. Manual refreshes only when an administrator selects Sync. Daily and weekly record when the next refresh is due for the scheduler; each connection can override this in its wizard." },
  notifications: { title: "Notification preferences", body: "Controls which integration outcomes should notify platform administrators. Use failed_syncs for exceptions only, all for every run, or none to rely on the Logs tab." },
  retry: { title: "Retry policy", body: "Describes how a failed scheduled request is attempted again. Use a small number of attempts with increasing delay to avoid repeatedly calling an unavailable provider." },
  timeout: { title: "Timeout seconds", body: "The maximum time a provider request may run before it is treated as failed. Increase this only when a provider regularly completes valid requests more slowly." },
  retention: { title: "Audit retention days", body: "How long Integration Hub activity and diagnostic evidence should be retained. Choose a period that matches the practice's security, support and compliance policy." },
  rotation: { title: "Secret rotation days", body: "The platform target for rotating provider credentials. Rotation replaces the encrypted secret while preserving a non-secret audit record of who changed it and when." },
};

function SettingLabel({ label, topic, onHelp }) {
  return <div className="flex items-center gap-1.5"><Label>{label}</Label><button type="button" aria-label={`About ${label}`} onClick={() => onHelp(topic)} className="rounded-full text-stone-400 hover:text-emerald-700"><HelpCircle className="h-4 w-4" /></button></div>;
}
